/**
 * VARLIK DÖKÜMÜ SIRALAMASI (Gelen kararlar 28.10.2026;
 * `claude/notlar/2026-10-28-ana-sayfa-kararlar.md` §4).
 *
 * - Sıralanan sütunlar: Varlık, Değer, Pay, Günlük, Maliyet, Mevcut getiri
 *   (28.10.2026 öncesinde başlığı "Gerçekleşmemiş"), Yıllık. Öbürleri sıralanmaz.
 * - Dokunuş artan / azalan arasında geçer; yeni sütun sayılarda azalanla
 *   (büyük üstte), Varlık'ta artanla (A→Z) başlar.
 * - Değeri "—" olan satırlar her iki yönde de en altta.
 * - Varsayılan: Değer, büyükten küçüğe.
 * - Tutar sütunları tutara göre (oranı değil); karşılaştırma ondalık
 *   metinle — para `number` ile hesaplanmaz.
 * - Eşitlikte motorun sırası korunur (`Array.prototype.sort` kararlıdır).
 */

import type { Decimal } from "@parafon/contracts";
import type { PositionValuation } from "@parafon/contracts/local-api";
import { decimalCompare } from "./decimal";

export type SiraSutunu = "varlik" | "deger" | "pay" | "gunluk" | "maliyet" | "mevcut" | "yillik";
export type SiraYonu = "artan" | "azalan";

export interface Siralama {
  readonly sutun: SiraSutunu;
  readonly yon: SiraYonu;
}

export const VARSAYILAN_SIRALAMA: Siralama = { sutun: "deger", yon: "azalan" };

type Anahtar = { readonly tur: "metin"; readonly v: string } | { readonly tur: "sayi"; readonly v: Decimal } | null;

function anahtar(p: PositionValuation, sutun: SiraSutunu, ad: (p: PositionValuation) => string): Anahtar {
  const sayi = (v: Decimal | null | undefined): Anahtar => (v === null || v === undefined ? null : { tur: "sayi", v });
  switch (sutun) {
    case "varlik":
      return { tur: "metin", v: ad(p) };
    case "deger":
      return sayi(p.reportValue?.amount);
    case "pay":
      return sayi(p.pay?.yuzde);
    case "gunluk":
      return sayi(p.gunluk?.tutar.amount);
    case "maliyet":
      return sayi(p.costConverted?.amount);
    case "mevcut":
      return sayi(p.unrealizedGain?.amount);
    case "yillik":
      return sayi(p.yillikKarsilik.yillik);
  }
}

/** Satırları sıralar; yeni dizi döner, girdiye dokunmaz. */
export function sirala(
  satirlar: readonly PositionValuation[],
  s: Siralama,
  ad: (p: PositionValuation) => string,
  yerel: string,
): PositionValuation[] {
  const yonlu = s.yon === "artan" ? 1 : -1;
  const metin = new Intl.Collator(yerel, { sensitivity: "base", numeric: true });
  return satirlar
    .map((p) => ({ p, k: anahtar(p, s.sutun, ad) }))
    .sort((a, b) => {
      // "—" her iki yönde en altta.
      if (a.k === null || b.k === null) return a.k === b.k ? 0 : a.k === null ? 1 : -1;
      const f =
        a.k.tur === "metin" && b.k.tur === "metin"
          ? metin.compare(a.k.v, b.k.v)
          : a.k.tur === "sayi" && b.k.tur === "sayi"
            ? decimalCompare(a.k.v, b.k.v)
            : 0;
      return f * yonlu;
    })
    .map((x) => x.p);
}

/** Başlığa dokununca: aynı sütunda yön değişir; yeni sütun kendi ilk yönüyle. */
export function sonrakiSiralama(onceki: Siralama, sutun: SiraSutunu): Siralama {
  if (onceki.sutun === sutun) return { sutun, yon: onceki.yon === "artan" ? "azalan" : "artan" };
  return { sutun, yon: sutun === "varlik" ? "artan" : "azalan" };
}
